import { ReportModel } from '@/http/models/report'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import { apiErrorMessage } from '@/lib/apiError'

import type { ReportDataName, ReportExportName, ReportRangeQuery } from '@/types/reports'

/** One report for a date range (and options such as sort or limit); keeps the last result while the next loads. */
export const useReport = <T,>(name: ReportDataName, query: Partial<ReportRangeQuery> & Record<string, unknown>) =>
  useQuery({
    queryKey: ['reports', name, query],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await ReportModel.getData<T>(name, query)
      if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not load the report'))
      return res.data.result
    },
  })

/** Downloads a report's Excel file for the range, named after the report and dates. */
export const useDownloadReport = () =>
  useMutation({
    mutationFn: async ({ name, range }: { name: ReportExportName; range: ReportRangeQuery }) => {
      const res = await ReportModel.download(name, { ...range })
      if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not download the report'))
      const url = URL.createObjectURL(res.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `${name}_${range.from_date}_${range.to_date}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    },
  })
